import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Boxes,
  ArrowDownToLine,
  PackageCheck,
  PackageOpen,
  Package,
  MoreVertical,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useMyGatherLists } from '@/api/hooks/useGatherLists';
import { useActiveWarehouse } from '@/hooks/useActiveWarehouse';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePermissions } from '@/hooks/usePermissions';

// Gather holds the slot Recount used to: a picker needs to SEE a gather list
// sent to them without leaving the pick flow, and Recount is still one tap away
// in the hamburger menu. Gather's icon matches the Gather screen's header.
const mobileNavItems = [
  { name: 'Home', href: '/', icon: LayoutDashboard },
  { name: 'Inventory', href: '/inventory', icon: Boxes },
  { name: 'Receive', href: '/purchase-orders', icon: ArrowDownToLine },
  { name: 'Pick', href: '/picking', icon: PackageCheck },
  { name: 'Gather', href: '/gather', icon: PackageOpen },
  { name: 'Wrapping', href: '/wrapping', icon: Package },
];

const GATHER_BADGE_POLL_MS = 60_000;

// The bar is `lg:hidden`, so the badge tracks Tailwind's `lg` (1024px), not
// useIsMobile's default 768 — tablets still show the bar.
const BAR_VISIBLE_BELOW_PX = 1024;

/**
 * How many gather lists have been SENT to me (`/mine` returns ASSIGNED/PRINTED
 * only — a draft stays off it until the manager releases it). Only asked when
 * the bar is on screen and the user may open Gather — `/mine` is PICKER+ and
 * would 403 every poll for a wrapper or recounter.
 *
 * Focus refetch is turned on HERE because it is off globally
 * (`lib/query-client.ts`): a picker unlocking the handheld should see a list
 * sent while it slept at once, not up to a minute later.
 */
function useMyGatherCount(): number {
  const { warehouseId } = useActiveWarehouse();
  const { canAccess } = usePermissions();
  const barVisible = useIsMobile(BAR_VISIBLE_BELOW_PX);
  const { data } = useMyGatherLists(warehouseId, {
    enabled: barVisible && canAccess('/gather'),
    refetchInterval: GATHER_BADGE_POLL_MS,
    refetchOnWindowFocus: true,
  });
  return data?.length ?? 0;
}

interface MobileNavProps {
  /**
   * Opens the full menu — the same sheet as the header's hamburger — so the
   * rest of the app (Recount, Counting, Scan…) is a thumb-reach away instead
   * of up top. One menu, already permission-filtered, never a second list.
   */
  onMoreClick: () => void;
  /** Whether that menu is open — drives `aria-expanded` on ⋮. */
  moreOpen: boolean;
}

export function MobileNav({ onMoreClick, moreOpen }: MobileNavProps) {
  const location = useLocation();
  const gatherCount = useMyGatherCount();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background lg:hidden"
      data-testid="mobile-nav"
    >
      {/* Six items plus the slim ⋮ have to share the width of a 360px phone,
          so each item gets an equal `flex-1` slice and its label truncates
          rather than the last item running off the right edge. ⋮ is a fixed
          narrow icon-only cell (a circled button) so it costs the labels as
          little as possible. */}
      <div className="flex h-[var(--mobile-nav-h)] items-stretch px-0.5">
        {mobileNavItems.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));
          const badgeCount = item.href === '/gather' ? gatherCount : 0;

          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={cn(
                'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px]',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-label={
                badgeCount > 0
                  ? `${item.name}, ${badgeCount} assigned`
                  : undefined
              }
              data-testid={`mobile-nav-${item.name.toLowerCase()}`}
            >
              {/* The badge sits on the ICON, not the cell, so it stays put
                  however wide the flex-1 slice gets. */}
              <span className="relative">
                <item.icon className="h-5 w-5 shrink-0" />
                {badgeCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -right-3 -top-2 h-4 min-w-[16px] justify-center px-1 text-[10px] leading-none"
                    data-testid={`mobile-nav-${item.name.toLowerCase()}-badge`}
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </Badge>
                )}
              </span>
              <span className="max-w-full truncate">{item.name}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={onMoreClick}
          className="group flex w-11 shrink-0 items-center justify-center text-muted-foreground"
          aria-label="More menu options"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          data-testid="mobile-nav-more"
        >
          {/* The circle makes ⋮ read as a button, not a seventh destination.
              The whole 44px cell (the touch-target minimum) is the tap target;
              the circle is just the visual. */}
          <span className="flex h-8 w-8 items-center justify-center rounded-full border bg-muted/60 group-active:bg-muted">
            <MoreVertical className="h-4 w-4" />
          </span>
        </button>
      </div>
    </nav>
  );
}
